import React, { memo, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom'; 
import { Box, Typography, Card, CardContent, IconButton, Chip, Divider, Tooltip, Checkbox, Button } from '@mui/material'; 
import Grid from "./ContainerGrid";
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import ListThumbnail from './ListThumbnail';
import { formatDisplayValue } from './DataListItems';

// =====================================================================
// 1. COMPONENT ITEM DẠNG THẺ (CARD LIST ITEM)
// =====================================================================
const DefaultListItem = memo(({ item, index, fields, context, onEdit, onCopy, onDelete, onView, hasImageField, renderItem, isSelectionMode, isItemSelected, onSelectionChange, history, renderRowActions }) => {
  const evaluateCondition = (condition, itemData) => { if (typeof condition === 'function') return condition(context, itemData); return !!condition; };
  const excludedFields = ['ten_trang_thai', 'color', 'picture', 'hinh_anh'];
  
  let titleField = fields.find(f => f.isTitle === true) || fields.find(f => f.type === 'String' && !excludedFields.includes(f.field)) || fields[0];
  
  // 👇 THAY ĐỔI: Tìm tất cả các field là SubTitle
  const subTitleFields = fields.filter(f => f.isSubTitle === true);

  // 👇 THAY ĐỔI: Lọc bỏ tất cả SubTitle khỏi danh sách field phụ
  let visibleFields = fields.filter(f => {
    if (f.field === titleField?.field || subTitleFields.some(sub => sub.field === f.field) || excludedFields.includes(f.field)) return false;
    if (evaluateCondition(f.hideInForm, item)) return false;
    const val = item[f.field];
    return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
  });
  visibleFields.sort((a, b) => (a.listOrder ?? 9999) - (b.listOrder ?? 9999));

  const statusColor = item.color || null;
  const statusLabel = item.ten_trang_thai || null;

  const handleCheckbox = (e) => {
    if (onSelectionChange) onSelectionChange(e, item._id);
  };

  const renderValue = (f, data) => {
    if (f.type === 'Action') {
      const actionFn = f.actionScript;
      return (
        <Button 
          variant="outlined" 
          size="small" 
          color="primary"
          sx={{ textTransform: 'none', mt: 0.5 }}
          onClick={(e) => {
            if(actionFn && typeof(actionFn)=="function"){
              e.stopPropagation();
              actionFn(context, data, history);
            }
          }}
        >
          {f.label || 'Thực thi'}
        </Button>
      );
    }

    const rawValue = formatDisplayValue(data[f.field], f, data, context);
    if (f.type === 'RichText') {
      return (
        <Box 
          component="div" 
          dangerouslySetInnerHTML={{ __html: rawValue }} 
          sx={{ 
            '& p': { m: 0 }, 
            '& *': { display: 'inline' } 
          }} 
        />
      );
    }
    return rawValue;
  };

  if (typeof renderItem === 'function') {
    return (
      <Box sx={{ position: 'relative', height: '100%' }}>
        {isSelectionMode && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <Checkbox checked={isItemSelected} onChange={handleCheckbox} size="small" />
          </Box>
        )}
        {renderItem(item, index, () => onEdit && onEdit(item), () => onDelete && onDelete(item._id), () => onView && onView(item), () => onCopy && onCopy(item))}
      </Box>
    );
  }

  return (
    <Card sx={{ position: 'relative', height: '100%', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.04)', border: '1px solid #e5e5ea', display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}>
      {isSelectionMode && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <Checkbox checked={isItemSelected} onChange={handleCheckbox} size="small" />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {statusColor && <Box sx={{ width: 4, bgcolor: statusColor, flexShrink: 0 }} />}
        <CardContent sx={{ p: '14px !important', flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: visibleFields.length > 0 ? 1 : 0 }}>
            {hasImageField && (
              <Box sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 2, overflow: 'hidden', bgcolor: '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', '& img': { width: '100%', height: '100%', objectFit: 'cover' } }}>
                <ListThumbnail item={item} fields={fields} context={context} altText={titleField ? String(item[titleField.field]) : "Image"} />
              </Box>
            )}
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, flexGrow: 1, pr: 1, overflow: 'hidden' }}>
                  <Typography component="div" variant="subtitle1" fontWeight="bold" color={"primary.main"} sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {titleField ? renderValue(titleField, item) : `Bản ghi #${index + 1}`}
                  </Typography>
                  
                  {/* 👇 THAY ĐỔI: Render mảng Subtitle trên cùng 1 dòng */}
                  {subTitleFields.length > 0 && (
                    <Typography component="div" variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {subTitleFields.map((f, idx) => (
                        <React.Fragment key={f.field}>
                          {renderValue(f, item)}
                          {idx < subTitleFields.length - 1 && (
                            <span style={{ margin: '0 6px', opacity: 0.4, fontSize: '0.9em' }}>•</span>
                          )}
                        </React.Fragment>
                      ))}
                    </Typography>
                  )}

                  {statusLabel && <Chip label={statusLabel} size="small" sx={{ mt: 0.5, alignSelf: 'flex-start', fontWeight: 'bold', fontSize: '0.65rem', height: 20, bgcolor: statusColor || '#8e8e93', color: 'white' }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {renderRowActions ? renderRowActions(item, 'list') : null}
                  {onView && <Tooltip title="Xem chi tiết"><IconButton size="small" onClick={() => onView(item)} sx={{ color: "primary.main", bgcolor: '#e3f2fd', p: 0.5 }}><VisibilityOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                  {onEdit && <Tooltip title="Chỉnh sửa"><IconButton size="small" onClick={() => onEdit(item)} sx={{ color: '#ff9500', bgcolor: '#fff3e0', p: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                  {onCopy && <Tooltip title="Nhân bản (Copy)"><IconButton size="small" onClick={() => onCopy(item)} sx={{ color: '#34c759', bgcolor: '#e8f5e9', p: 0.5 }}><ContentCopyOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                  {onDelete && <Tooltip title="Xoá"><IconButton size="small" onClick={() => onDelete(item._id)} sx={{ color: '#ff3b30', bgcolor: '#ffebee', p: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                </Box>
              </Box>
            </Box>
          </Box>
          
          {visibleFields.length > 0 && (
            <Box sx={{ mt: 'auto', pt: 1 }}>
              <Divider sx={{ mb: 1.5, borderStyle: 'dashed', borderColor: '#e5e5ea' }} />
              <Grid container spacing={1.5}>
                {visibleFields.map(f => (
                  <Grid size={{ xs: 6 }} key={f.field}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, mb: 0.2 }}>{f.label}</Typography>
                      <Typography 
                        component="div" 
                        variant="body2" 
                        color="#1c1c1e" 
                        fontWeight="500" 
                        sx={{ 
                            wordBreak: 'break-word', 
                            lineHeight: 1.3, 
                            display: '-webkit-box', 
                            WebkitLineClamp: 2, 
                            WebkitBoxOrient: 'vertical', 
                            overflow: 'hidden' 
                        }}
                      >
                        {renderValue(f, item)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Box>
    </Card>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.isItemSelected === nextProps.isItemSelected &&
    prevProps.isSelectionMode === nextProps.isSelectionMode &&
    prevProps.renderRowActions === nextProps.renderRowActions
  );
});

// =====================================================================
// 2. COMPONENT LIST DẠNG LƯỚI BAO BỌC BÊN NGOÀI
// =====================================================================
export default function CardListView({
  data, fields, gridProps,
  isSelectionMode,
  selectedIds, onSelectionChange,
  onView, onEdit, onCopy, onDelete, canEdit, canCopy, canDelete, context, renderItem,
  renderRowActions,   // nút tuỳ chỉnh của dòng (rowActions)
}) {
  const history = useHistory();
  const hasImageField = fields.some(f => f.field === 'picture' || f.field === 'hinh_anh' || f.type === 'File' || f.type === 'FileArray');

  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const handleToggleSingleCard = useCallback((event, id) => {
    const currentSelected = selectedIdsRef.current;
    const selectedIndex = currentSelected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) newSelected = newSelected.concat(currentSelected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(currentSelected.slice(1));
    else if (selectedIndex === currentSelected.length - 1) newSelected = newSelected.concat(currentSelected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(currentSelected.slice(0, selectedIndex), currentSelected.slice(selectedIndex + 1));

    if (onSelectionChange) onSelectionChange(newSelected);
  }, [onSelectionChange]);

  return (
    <Grid container spacing={2}>
      {data.map((item, index) => {
        const isItemSelected = selectedIds?.indexOf(item._id) !== -1;
        return (
          <Grid
            size={gridProps} key={item._id || index}
            sx={{ display: 'flex', flexDirection: 'column', '& > *': { flexGrow: 1, height: '100%' } }}
          >
            <DefaultListItem
              renderItem={renderItem}
              isItemSelected={isItemSelected}
              onSelectionChange={onSelectionChange ? handleToggleSingleCard : null}
              isSelectionMode={isSelectionMode}
              item={item}
              index={index}
              fields={fields}
              context={context}
              history={history} 
              onEdit={canEdit ? onEdit : null}
              onDelete={canDelete ? onDelete : null}
              onCopy={canCopy ? onCopy : null}
              onView={onView}
              hasImageField={hasImageField}
              renderRowActions={renderRowActions}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}